import { Button, Grid2 as Grid, Typography } from "@mui/material"
import Stack from "@mui/material/Stack"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useTranslation } from "react-i18next"
import Layout from "../components/Layout"
import SequenzaFormPaper from "../components/forms/SequenzaFormPaper"
import { useQsFilters } from "../hooks/filters"
import { useSequenzaCreate, useSequenzeList, useSequenzaDelete, useSequenzaUpdate } from "../hooks/sequenze"
import { FileDigitale, Sequenza } from "../types"
import SequenzaCard from "../components/SequenzaCard"
import DialogConfirm from "../components/DialogConfirm"
import { useSnackbarMessage } from "../hooks/snackbar"
import SequenzePlayer from "../components/SequenzePlayer"
import { Fullscreen, FullscreenExit } from "@mui/icons-material"
import { useRef, useState } from "react"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useIsMobile } from "../hooks/mobile"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  formato: params.get("formato") ?? "",
  elemento: params.get("elemento") ?? "",
  seq: params.get("seq") ?? "",
})

type SequenzeProps = {
  fileDigitale: FileDigitale | undefined
  fileDigitaleId: number
}

type PlayerType = {
  getCurrentTime: () => number
  setPlayed: (value: number) => void
}

export default function Sequenze({ fileDigitaleId, fileDigitale }: SequenzeProps) {
  const { t } = useTranslation()
  const { filters } = useQsFilters(initFilters)
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const { data } = useSequenzeList({
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
    file_digitale: fileDigitaleId.toString(),
  })

  const { mutateAsync: create } = useSequenzaCreate()
  const { mutateAsync: update } = useSequenzaUpdate()
  const { mutateAsync: remove } = useSequenzaDelete()

  const [createModalState, createModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const playerRef = useRef<PlayerType>(null)
  const fullScreenRef = useRef<HTMLDivElement>(null)

  const [fullScreen, setFullScreen] = useState(false)
  const [currentSequence, setCurrentSequence] = useState<Sequenza | null>(null)

  const handleFullScreen = () => {
    if (!fullScreenRef.current) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
      setFullScreen(false)
    } else {
      fullScreenRef.current
        .requestFullscreen()
        .catch((err) => {
          console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`)
          setFullScreen(false)
        })
        .then(() => {
          setFullScreen(true)
        })
    }
  }

  return (
    <Layout>
      <Stack sx={{ minHeight: "100%", backgroundColor: "grey.100" }} ref={fullScreenRef}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            textAlign: "right",
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "grey.100",
            borderBottom: 1,
            borderColor: "grey.300",
          }}
          px={3}
          py={1}
        >
          {!isMobile && (
            <Stack direction="row" spacing={1} alignItems="center">
              {
                <Button onClick={handleFullScreen} size="small">
                  {fullScreen ? (
                    <FullscreenExit />
                  ) : (
                    <Stack direction={"row"} alignItems={"center"}>
                      <Fullscreen sx={{ mr: 1 }} /> Fullscreen
                    </Stack>
                  )}
                </Button>
              }
              {fullScreen && <Typography variant="h6">{fileDigitale?.filename}</Typography>}
            </Stack>
          )}
          {!createModalState.isOpen && userCanWriteAndDelete && !isMobile && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("sequenza.cta_aggiungi")}
            </Button>
          )}
        </Stack>

        <Stack flexGrow={1} px={3} pb={3} pt={1}>
          <Grid flexGrow={1} container spacing={2} flexDirection={isMobile ? "column" : "row"}>
            <Grid flexGrow={1} size={isMobile ? 12 : fullScreen ? 8 : 6}>
              {fileDigitale && (
                <SequenzePlayer
                  fileDigitale={fileDigitale}
                  sequenze={data?.results}
                  ref={playerRef}
                  fullScreen={fullScreen}
                  setCurrentSequence={setCurrentSequence}
                  filterSequence={filters.seq}
                />
              )}
            </Grid>
            <Grid size={isMobile ? 12 : fullScreen ? 4 : 6} sx={{ position: "relative" }}>
              {!createModalState.isOpen && (
                <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 0, overflowY: "auto" }}>
                  <Stack spacing={1} sx={{mb: 2}}>
                    {data?.results.map((sequenza) => (
                      <SequenzaCard
                        key={sequenza.id}
                        currentSequence={currentSequence}
                        sequenza={sequenza}
                        onUpdate={() => {
                          createModalActions.open(sequenza)
                        }}
                        onDelete={() => {
                          deleteModalActions.open(sequenza)
                        }}
                      />
                    ))}
                  </Stack>
                </div>
              )}

              {createModalState.isOpen && (
                <SequenzaFormPaper
                  getCurrentTime={() => {
                    if (!playerRef.current) return 0
                    const ct = playerRef.current.getCurrentTime()
                    return ct
                  }}
                  item={createModalState.value}
                  save={(values: Sequenza) => {
                    const newValues = {
                      ...values,
                      tcin: values.tcin.trim(),
                      tcout: values.tcout.trim(),
                      file_digitale: fileDigitaleId,
                    }
                    const method = createModalState.value ? update : create
                    return method(newValues as Sequenza).then(() => {
                      createModalActions.close()
                    })
                  }}
                  handleClose={() => {
                    createModalActions.close()
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Stack>
      </Stack>

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("sequenza.cta_elimina")}
        subtitle={
          <span>
            {t("sequenza.cta_subtitle_elimina")} <b>"{deleteModalState.value?.data}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("sequenza.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
